import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Divider,
  Box,
  Typography,
} from "@mui/material";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  FaEye,
  FaSearch,
  FaFileAlt,
  FaUndo,
  FaFilePdf,
  FaFileExcel,
  FaChartBar,
  FaChartPie,
  FaSave,
  FaFilter,
  FaColumns,
  FaSortAmountDown,
} from "react-icons/fa";
import { DatePicker, Space } from "antd";
import { gbState } from "../../../components/shared/GlobalState";
import useFetch from "../../../components/shared/useFetch";
import Modal from "../../../components/Modal/Modal";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Sector,
} from "recharts";

const ExpenseFlexiReportsChild = () => {
  const navigate = useNavigate();
  const [{ user }] = useAtom(gbState);
  const [expenseData, setExpenseData] = useState([]);

  // Report configuration state
  const [dateRange, setDateRange] = useState([null, null]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [amountRange, setAmountRange] = useState([0, 10000]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([
    "expense_title",
    "category",
    "date",
    "amount",
    "transfer_source",
  ]);
  const [groupBy, setGroupBy] = useState(null);
  const [sortBy, setSortBy] = useState("date");
  const [sortDirection, setSortDirection] = useState("desc");

  // UI state
  const [showFilters, setShowFilters] = useState(false);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [viewMode, setViewMode] = useState("table"); // table, bar, pie
  const [savedConfigurations, setSavedConfigurations] = useState([]);
  const [configName, setConfigName] = useState("");
  const [showSaveModal, setShowSaveModal] = useState(false);

  // Fetch data
  const urlLink = `${process.env.REACT_APP_API_URL}/expense/${user.church_id}`;
  const { data, loading, error, refetch } = useFetch(urlLink);

  // Process data
  useEffect(() => {
    if (data?.expenses) {
      const updatedData = data.expenses.map((expense) => ({
        ...expense,
        id: expense._id,
        category: expense.category_id?.category_name,
      }));
      setExpenseData(updatedData);

      // Extract unique payment methods
      const methods = [
        ...new Set(
          updatedData.map((item) => item.transfer_source).filter(Boolean)
        ),
      ];
      setPaymentMethods(methods);

      // Extract unique categories
      const categories = [
        ...new Set(updatedData.map((item) => item.category).filter(Boolean)),
      ];
      setSelectedCategories(categories);
    }
  }, [data]);

  // Apply filters to get filtered data
  const filteredData = useMemo(() => {
    if (!expenseData) return [];

    return expenseData.filter((expense) => {
      // Date filter
      if (dateRange && dateRange[0] && dateRange[1]) {
        const expenseDate = new Date(expense.date);
        const startDate = new Date(dateRange[0]);
        const endDate = new Date(dateRange[1]);
        if (expenseDate < startDate || expenseDate > endDate) return false;
      }

      // Category filter
      if (
        selectedCategories &&
        selectedCategories.length > 0 &&
        !selectedCategories.includes(expense.category)
      ) {
        return false;
      }

      // Amount filter
      if (
        amountRange &&
        amountRange[0] !== undefined &&
        amountRange[1] !== undefined &&
        (expense.amount < amountRange[0] || expense.amount > amountRange[1])
      ) {
        return false;
      }

      // Payment method filter
      if (
        paymentMethods &&
        paymentMethods.length > 0 &&
        !paymentMethods.includes(expense.transfer_source)
      ) {
        return false;
      }

      return true;
    });
  }, [expenseData, dateRange, selectedCategories, amountRange, paymentMethods]);

  // Group data if grouping is selected
  const groupedData = useMemo(() => {
    if (!groupBy) return filteredData;

    const groups = {};
    filteredData.forEach((expense) => {
      const key = expense[groupBy] || "Unknown";
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(expense);
    });

    return Object.entries(groups).map(([key, items]) => ({
      groupKey: key,
      items,
      totalAmount: items.reduce((sum, item) => sum + item.amount, 0),
      count: items.length,
    }));
  }, [filteredData, groupBy]);

  // Sort data
  const sortedData = useMemo(() => {
    if (groupBy) {
      return [...groupedData].sort((a, b) => {
        if (sortBy === "totalAmount") {
          return sortDirection === "asc"
            ? a.totalAmount - b.totalAmount
            : b.totalAmount - a.totalAmount;
        }
        if (sortBy === "count") {
          return sortDirection === "asc"
            ? a.count - b.count
            : b.count - a.count;
        }
        return sortDirection === "asc"
          ? a.groupKey.localeCompare(b.groupKey)
          : b.groupKey.localeCompare(a.groupKey);
      });
    }

    return [...filteredData].sort((a, b) => {
      if (sortBy === "amount") {
        return sortDirection === "asc"
          ? a.amount - b.amount
          : b.amount - a.amount;
      }
      if (sortBy === "date") {
        return sortDirection === "asc"
          ? new Date(a.date) - new Date(b.date)
          : new Date(b.date) - new Date(a.date);
      }
      return sortDirection === "asc"
        ? String(a[sortBy] || "").localeCompare(String(b[sortBy] || ""))
        : String(b[sortBy] || "").localeCompare(String(a[sortBy] || ""));
    });
  }, [filteredData, groupedData, groupBy, sortBy, sortDirection]);

  // Reset all filters
  const resetFilters = () => {
    setDateRange([null, null]);
    setSelectedCategories([
      ...new Set(expenseData.map((item) => item.category).filter(Boolean)),
    ]);
    setAmountRange([0, 10000]);
    setPaymentMethods([
      ...new Set(
        expenseData.map((item) => item.transfer_source).filter(Boolean)
      ),
    ]);
    setSelectedColumns([
      "expense_title",
      "category",
      "date",
      "amount",
      "transfer_source",
    ]);
    setGroupBy(null);
    setSortBy("date");
    setSortDirection("desc");
    setViewMode("table");
  };

  // Save configuration
  const saveConfiguration = () => {
    if (!configName) return;

    const newConfig = {
      name: configName,
      config: {
        dateRange,
        selectedCategories,
        amountRange,
        paymentMethods,
        selectedColumns,
        groupBy,
        sortBy,
        sortDirection,
        viewMode,
      },
    };

    const updatedConfigs = [...savedConfigurations, newConfig];
    setSavedConfigurations(updatedConfigs);
    localStorage.setItem(
      "expenseReportConfigs",
      JSON.stringify(updatedConfigs)
    );
    setShowSaveModal(false);
    setConfigName("");
  };

  // Load configuration
  const loadConfiguration = (config) => {
    setDateRange(config.dateRange);
    setSelectedCategories(config.selectedCategories);
    setAmountRange(config.amountRange);
    setPaymentMethods(config.paymentMethods);
    setSelectedColumns(config.selectedColumns);
    setGroupBy(config.groupBy);
    setSortBy(config.sortBy);
    setSortDirection(config.sortDirection);
    setViewMode(config.viewMode);
  };

  // Load saved configurations from localStorage
  useEffect(() => {
    const savedConfigs = localStorage.getItem("expenseReportConfigs");
    if (savedConfigs) {
      setSavedConfigurations(JSON.parse(savedConfigs));
    }
  }, []);

  // Calculate summary statistics
  const summary = useMemo(() => {
    const total = filteredData.reduce((sum, item) => sum + item.amount, 0);
    const avg = filteredData.length > 0 ? total / filteredData.length : 0;
    const max =
      filteredData.length > 0
        ? Math.max(...filteredData.map((item) => item.amount))
        : 0;
    const min =
      filteredData.length > 0
        ? Math.min(...filteredData.map((item) => item.amount))
        : 0;

    return { total, avg, max, min, count: filteredData.length };
  }, [filteredData]);

  // Prepare data for charts
  const chartData = useMemo(() => {
    if (groupBy) {
      // Use the grouped data for charts
      return sortedData.map((group) => ({
        name: group.groupKey,
        value: group.totalAmount,
        count: group.count,
      }));
    } else {
      // If not grouped, group by category for charts
      const categoryGroups = {};
      filteredData.forEach((expense) => {
        const category = expense.category || "Uncategorized";
        if (!categoryGroups[category]) {
          categoryGroups[category] = {
            name: category,
            value: 0,
            count: 0,
          };
        }
        categoryGroups[category].value += expense.amount;
        categoryGroups[category].count += 1;
      });

      return Object.values(categoryGroups);
    }
  }, [filteredData, sortedData, groupBy]);

  // Colors for charts
  const COLORS = [
    "#0088FE",
    "#00C49F",
    "#FFBB28",
    "#FF8042",
    "#8884D8",
    "#82CA9D",
    "#FFC658",
    "#8DD1E1",
    "#A4DE6C",
    "#D0ED57",
  ];

  // Active pie sector state
  const [activeIndex, setActiveIndex] = useState(0);
  const onPieEnter = useCallback((_, index) => {
    setActiveIndex(index);
  }, []);

  // Custom pie chart active shape
  const renderActiveShape = (props) => {
    const RADIAN = Math.PI / 180;
    const {
      cx,
      cy,
      midAngle,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      payload,
      percent,
      value,
    } = props;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 30) * cos;
    const my = cy + (outerRadius + 30) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 22;
    const ey = my;
    const textAnchor = cos >= 0 ? "start" : "end";

    return (
      <g>
        <text x={cx} y={cy} dy={8} textAnchor="middle" fill={fill}>
          {payload.name}
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 6}
          outerRadius={outerRadius + 10}
          fill={fill}
        />
        <path
          d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
          stroke={fill}
          fill="none"
        />
        <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
        <text
          x={ex + (cos >= 0 ? 1 : -1) * 12}
          y={ey}
          textAnchor={textAnchor}
          fill="#333"
        >{`$${value.toFixed(2)}`}</text>
        <text
          x={ex + (cos >= 0 ? 1 : -1) * 12}
          y={ey}
          dy={18}
          textAnchor={textAnchor}
          fill="#999"
        >
          {`(${(percent * 100).toFixed(2)}%)`}
        </text>
      </g>
    );
  };

  // Export to PDF function
  const exportToPDF = () => {
    const doc = new jsPDF();

    // Add title
    doc.text("Expense Report", 14, 16);

    if (groupBy) {
      // For grouped data
      const tableColumn = [groupBy, "Count", "Total Amount"];
      const tableRows = sortedData.map((group) => [
        group.groupKey,
        group.count,
        `$${group.totalAmount.toFixed(2)}`,
      ]);

      doc.autoTable({
        head: [tableColumn],
        body: tableRows,
        startY: 20,
      });
    } else {
      // For regular data
      const headers = {
        expense_title: "Title",
        category: "Category",
        date: "Date",
        amount: "Amount",
        transfer_source: "Payment Method",
        description: "Description",
        voucher_number: "Voucher #",
        financial_year: "Financial Year",
      };

      const tableColumn = selectedColumns.map((col) => headers[col] || col);

      const tableRows = sortedData.map((expense) => {
        return selectedColumns.map((col) => {
          if (col === "date") {
            return new Date(expense[col])
              .toLocaleDateString("en-US", {
                month: "2-digit",
                day: "2-digit",
                year: "numeric",
              })
              .replace(/\//g, "-");
          } else if (col === "amount") {
            return `$${expense[col].toFixed(2)}`;
          } else {
            return expense[col] || "";
          }
        });
      });

      doc.autoTable({
        head: [tableColumn],
        body: tableRows,
        startY: 20,
      });
    }

    // Add summary page
    doc.addPage();
    doc.text("Expense Summary", 14, 16);

    const summaryColumn = ["Statistic", "Value"];
    const summaryRows = [
      ["Total Expenses", `$${summary.total.toFixed(2)}`],
      ["Average Expense", `$${summary.avg.toFixed(2)}`],
      ["Maximum Expense", `$${summary.max.toFixed(2)}`],
      ["Minimum Expense", `$${summary.min.toFixed(2)}`],
      ["Count", summary.count.toString()],
    ];

    doc.autoTable({
      head: [summaryColumn],
      body: summaryRows,
      startY: 20,
    });

    // Add filter information if filters are applied
    const filterRows = [];

    if (dateRange[0] && dateRange[1]) {
      filterRows.push([
        "Date Range",
        `${dateRange[0]
          .toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          })
          .replace(/\//g, "-")} - ${dateRange[1]
          .toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          })
          .replace(/\//g, "-")}`,
      ]);
    }

    if (
      selectedCategories.length > 0 &&
      selectedCategories.length < expenseData.length
    ) {
      filterRows.push(["Categories", selectedCategories.join(", ")]);
    }

    if (amountRange[0] > 0 || amountRange[1] < 10000) {
      filterRows.push([
        "Amount Range",
        `$${amountRange[0]} - $${amountRange[1]}`,
      ]);
    }

    if (filterRows.length > 0) {
      doc.text("Applied Filters", 14, doc.autoTable.previous.finalY + 10);
      doc.autoTable({
        head: [["Filter", "Value"]],
        body: filterRows,
        startY: doc.autoTable.previous.finalY + 15,
      });
    }

    doc.save(`Expense_Report_${new Date().toISOString().split("T")[0]}.pdf`);
  };

  // Export to Excel function
  const exportToExcel = () => {
    let excelData;
    let sheetName;

    if (groupBy) {
      // For grouped data
      sheetName = `Expenses by ${groupBy}`;
      excelData = sortedData.map((group) => ({
        [groupBy]: group.groupKey,
        Count: group.count,
        "Total Amount": group.totalAmount,
      }));
    } else {
      // For regular data
      sheetName = "Expenses";
      excelData = sortedData.map((expense) => {
        const row = {};
        selectedColumns.forEach((col) => {
          const headers = {
            expense_title: "Title",
            category: "Category",
            date: "Date",
            amount: "Amount",
            transfer_source: "Payment Method",
            description: "Description",
            voucher_number: "Voucher #",
            financial_year: "Financial Year",
          };

          if (col === "date") {
            row[headers[col]] = new Date(expense[col])
              .toLocaleDateString("en-US", {
                month: "2-digit",
                day: "2-digit",
                year: "numeric",
              })
              .replace(/\//g, "-");
          } else if (col === "amount") {
            row[headers[col]] = expense[col];
          } else {
            row[headers[col]] = expense[col] || "";
          }
        });
        return row;
      });
    }

    // Add summary sheet
    const summaryData = [
      { Statistic: "Total Expenses", Value: summary.total },
      { Statistic: "Average Expense", Value: summary.avg },
      { Statistic: "Maximum Expense", Value: summary.max },
      { Statistic: "Minimum Expense", Value: summary.min },
      { Statistic: "Count", Value: summary.count },
    ];

    // Add filter information
    const filterData = [];

    if (dateRange[0] && dateRange[1]) {
      filterData.push({
        Filter: "Date Range",
        Value: `${dateRange[0]
          .toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          })
          .replace(/\//g, "-")} - ${dateRange[1]
          .toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          })
          .replace(/\//g, "-")}`,
      });
    }

    if (
      selectedCategories.length > 0 &&
      selectedCategories.length < expenseData.length
    ) {
      filterData.push({
        Filter: "Categories",
        Value: selectedCategories.join(", "),
      });
    }

    if (amountRange[0] > 0 || amountRange[1] < 10000) {
      filterData.push({
        Filter: "Amount Range",
        Value: `$${amountRange[0]} - $${amountRange[1]}`,
      });
    }

    // Create workbook with multiple sheets
    const wb = XLSX.utils.book_new();

    // Add main data sheet
    const ws = XLSX.utils.json_to_sheet(excelData);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    // Add summary sheet
    const summaryWs = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summaryWs, "Summary");

    // Add filters sheet if there are filters applied
    if (filterData.length > 0) {
      const filterWs = XLSX.utils.json_to_sheet(filterData);
      XLSX.utils.book_append_sheet(wb, filterWs, "Filters");
    }

    // Save the Excel file
    XLSX.writeFile(
      wb,
      `Expense_Report_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  return (
    <div className="w-full p-6 bg-white rounded-lg shadow-md">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">
          Expense Flexible Reports
        </h2>
        <div className="flex space-x-2">
          <Button
            variant="outlined"
            startIcon={<FaFilter />}
            onClick={() => setShowFilters(!showFilters)}
            className="text-blue-500 border-blue-500 hover:bg-blue-50"
          >
            Filters
          </Button>
          <Button
            variant="outlined"
            startIcon={<FaColumns />}
            onClick={() => setShowColumnSelector(!showColumnSelector)}
            className="text-purple-500 border-purple-500 hover:bg-purple-50"
          >
            Columns
          </Button>
          <Button
            variant="outlined"
            startIcon={<FaSave />}
            onClick={() => setShowSaveModal(true)}
            className="text-green-500 border-green-500 hover:bg-green-50"
          >
            Save
          </Button>
          <Button
            variant="outlined"
            startIcon={<FaUndo />}
            onClick={resetFilters}
            className="text-red-500 border-red-500 hover:bg-red-50"
          >
            Reset
          </Button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="p-4 mb-6 rounded-lg bg-gray-50">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* First Row - 2 columns */}
            <div>
              <p className="mb-1 text-sm font-medium text-gray-700">
                Date Range
              </p>
              <DatePicker.RangePicker
                value={dateRange}
                onChange={setDateRange}
                format="MM-DD-YYYY"
                className="w-full"
              />
            </div>

            <div>
              <p className="mb-1 text-sm font-medium text-gray-700">
                Amount Range
              </p>
              <div className="flex items-center space-x-2">
                <TextField
                  type="number"
                  value={amountRange[0]}
                  onChange={(e) =>
                    setAmountRange([Number(e.target.value), amountRange[1]])
                  }
                  size="small"
                  className="w-1/2"
                />
                <span>to</span>
                <TextField
                  type="number"
                  value={amountRange[1]}
                  onChange={(e) =>
                    setAmountRange([amountRange[0], Number(e.target.value)])
                  }
                  size="small"
                  className="w-1/2"
                />
              </div>
            </div>

            {/* Second Row - 2 columns */}
            <div>
              <p className="mb-1 text-sm font-medium text-gray-700">
                Categories
              </p>
              <FormControl size="small" fullWidth>
                <Select
                  multiple
                  value={selectedCategories}
                  onChange={(e) => setSelectedCategories(e.target.value)}
                  renderValue={(selected) => selected.join(", ")}
                  displayEmpty
                >
                  {[
                    ...new Set(
                      expenseData.map((item) => item.category).filter(Boolean)
                    ),
                  ].map((category) => (
                    <MenuItem key={category} value={category}>
                      <Checkbox
                        checked={selectedCategories.indexOf(category) > -1}
                      />
                      <span>{category}</span>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div>
              <p className="mb-1 text-sm font-medium text-gray-700">
                Payment Methods
              </p>
              <FormControl size="small" fullWidth>
                <Select
                  multiple
                  value={paymentMethods}
                  onChange={(e) => setPaymentMethods(e.target.value)}
                  renderValue={(selected) => selected.join(", ")}
                  displayEmpty
                >
                  {[
                    ...new Set(
                      expenseData
                        .map((item) => item.transfer_source)
                        .filter(Boolean)
                    ),
                  ].map((method) => (
                    <MenuItem key={method} value={method}>
                      <Checkbox checked={paymentMethods.indexOf(method) > -1} />
                      <span>{method}</span>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            {/* Third Row - 2 columns */}
            <div>
              <p className="mb-1 text-sm font-medium text-gray-700">Group By</p>
              <FormControl size="small" fullWidth>
                <Select
                  value={groupBy || ""}
                  onChange={(e) => setGroupBy(e.target.value || null)}
                  displayEmpty
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="category">Category</MenuItem>
                  <MenuItem value="transfer_source">Payment Method</MenuItem>
                  <MenuItem value="financial_year">Financial Year</MenuItem>
                </Select>
              </FormControl>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="mb-1 text-sm font-medium text-gray-700">
                  Sort By
                </p>
                <FormControl size="small" fullWidth>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="date">Date</MenuItem>
                    <MenuItem value="amount">Amount</MenuItem>
                    <MenuItem value="expense_title">Title</MenuItem>
                    <MenuItem value="category">Category</MenuItem>
                    {groupBy && (
                      <MenuItem value="totalAmount">Total Amount</MenuItem>
                    )}
                    {groupBy && <MenuItem value="count">Count</MenuItem>}
                  </Select>
                </FormControl>
              </div>

              <div>
                <p className="mb-1 text-sm font-medium text-gray-700">
                  Sort Direction
                </p>
                <FormControl size="small" fullWidth>
                  <Select
                    value={sortDirection}
                    onChange={(e) => setSortDirection(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="asc">Ascending</MenuItem>
                    <MenuItem value="desc">Descending</MenuItem>
                  </Select>
                </FormControl>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Column Selector */}
      {showColumnSelector && (
        <div className="p-4 mb-6 rounded-lg bg-gray-50">
          <p className="mb-2 text-sm font-medium text-gray-700">
            Select Columns to Display
          </p>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedColumns.includes("expense_title")}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedColumns([...selectedColumns, "expense_title"]);
                    } else {
                      setSelectedColumns(
                        selectedColumns.filter((col) => col !== "expense_title")
                      );
                    }
                  }}
                />
              }
              label="Title"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedColumns.includes("category")}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedColumns([...selectedColumns, "category"]);
                    } else {
                      setSelectedColumns(
                        selectedColumns.filter((col) => col !== "category")
                      );
                    }
                  }}
                />
              }
              label="Category"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedColumns.includes("date")}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedColumns([...selectedColumns, "date"]);
                    } else {
                      setSelectedColumns(
                        selectedColumns.filter((col) => col !== "date")
                      );
                    }
                  }}
                />
              }
              label="Date"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedColumns.includes("amount")}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedColumns([...selectedColumns, "amount"]);
                    } else {
                      setSelectedColumns(
                        selectedColumns.filter((col) => col !== "amount")
                      );
                    }
                  }}
                />
              }
              label="Amount"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedColumns.includes("transfer_source")}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedColumns([
                        ...selectedColumns,
                        "transfer_source",
                      ]);
                    } else {
                      setSelectedColumns(
                        selectedColumns.filter(
                          (col) => col !== "transfer_source"
                        )
                      );
                    }
                  }}
                />
              }
              label="Payment Method"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedColumns.includes("description")}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedColumns([...selectedColumns, "description"]);
                    } else {
                      setSelectedColumns(
                        selectedColumns.filter((col) => col !== "description")
                      );
                    }
                  }}
                />
              }
              label="Description"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedColumns.includes("voucher_number")}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedColumns([
                        ...selectedColumns,
                        "voucher_number",
                      ]);
                    } else {
                      setSelectedColumns(
                        selectedColumns.filter(
                          (col) => col !== "voucher_number"
                        )
                      );
                    }
                  }}
                />
              }
              label="Voucher #"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedColumns.includes("financial_year")}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedColumns([
                        ...selectedColumns,
                        "financial_year",
                      ]);
                    } else {
                      setSelectedColumns(
                        selectedColumns.filter(
                          (col) => col !== "financial_year"
                        )
                      );
                    }
                  }}
                />
              }
              label="Financial Year"
            />
          </div>
        </div>
      )}

      {/* Summary Statistics */}
      <div className="p-4 mb-6 rounded-lg bg-blue-50">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
          <div className="p-3 bg-white rounded-md shadow-sm">
            <p className="text-xs text-gray-500">Total Expenses</p>
            <p className="text-xl font-semibold">${summary.total.toFixed(2)}</p>
          </div>
          <div className="p-3 bg-white rounded-md shadow-sm">
            <p className="text-xs text-gray-500">Average</p>
            <p className="text-xl font-semibold">${summary.avg.toFixed(2)}</p>
          </div>
          <div className="p-3 bg-white rounded-md shadow-sm">
            <p className="text-xs text-gray-500">Maximum</p>
            <p className="text-xl font-semibold">${summary.max.toFixed(2)}</p>
          </div>
          <div className="p-3 bg-white rounded-md shadow-sm">
            <p className="text-xs text-gray-500">Minimum</p>
            <p className="text-xl font-semibold">${summary.min.toFixed(2)}</p>
          </div>
          <div className="p-3 bg-white rounded-md shadow-sm">
            <p className="text-xs text-gray-500">Count</p>
            <p className="text-xl font-semibold">{summary.count}</p>
          </div>
        </div>
      </div>

      {/* View Mode Selector */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex space-x-2">
          <Button
            variant={viewMode === "table" ? "contained" : "outlined"}
            onClick={() => setViewMode("table")}
            className={viewMode === "table" ? "bg-blue-600" : ""}
          >
            Table
          </Button>
          <Button
            variant={viewMode === "bar" ? "contained" : "outlined"}
            onClick={() => setViewMode("bar")}
            className={viewMode === "bar" ? "bg-blue-600" : ""}
          >
            Bar Chart
          </Button>
          <Button
            variant={viewMode === "pie" ? "contained" : "outlined"}
            onClick={() => setViewMode("pie")}
            className={viewMode === "pie" ? "bg-blue-600" : ""}
          >
            Pie Chart
          </Button>
        </div>

        <div className="flex space-x-2">
          <Button
            variant="contained"
            startIcon={<FaFilePdf />}
            onClick={() => exportToPDF()}
            className="bg-red-600 hover:bg-red-700"
          >
            PDF
          </Button>
          <Button
            variant="contained"
            startIcon={<FaFileExcel />}
            onClick={() => exportToExcel()}
            className="bg-green-600 hover:bg-green-700"
          >
            Excel
          </Button>
        </div>
      </div>

      {/* Results Display */}
      <div className="w-full bg-white border border-gray-200 rounded-lg">
        {loading ? (
          <div className="p-8 text-center">Loading...</div>
        ) : error ? (
          <div className="p-8 text-center text-red-500">Error loading data</div>
        ) : (
          <>
            {viewMode === "table" && (
              <TableContainer
                component={Paper}
                className="max-h-[600px] w-full"
              >
                <Table stickyHeader className="w-full">
                  <TableHead>
                    <TableRow>
                      {groupBy && (
                        <TableCell className="font-semibold bg-gray-100">
                          {groupBy}
                        </TableCell>
                      )}
                      {groupBy && (
                        <TableCell className="font-semibold bg-gray-100">
                          Count
                        </TableCell>
                      )}
                      {groupBy && (
                        <TableCell className="font-semibold bg-gray-100">
                          Total Amount
                        </TableCell>
                      )}

                      {!groupBy &&
                        selectedColumns.map((column) => {
                          const headers = {
                            expense_title: "Title",
                            category: "Category",
                            date: "Date",
                            amount: "Amount",
                            transfer_source: "Payment Method",
                            description: "Description",
                            voucher_number: "Voucher #",
                            financial_year: "Financial Year",
                          };

                          return (
                            <TableCell
                              key={column}
                              className="font-semibold bg-gray-100"
                            >
                              {headers[column] || column}
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (sortBy === column) {
                                    setSortDirection(
                                      sortDirection === "asc" ? "desc" : "asc"
                                    );
                                  } else {
                                    setSortBy(column);
                                    setSortDirection("asc");
                                  }
                                }}
                              >
                                <FaSortAmountDown
                                  className={
                                    sortBy === column
                                      ? "text-blue-500"
                                      : "text-gray-400"
                                  }
                                />
                              </IconButton>
                            </TableCell>
                          );
                        })}
                      <TableCell className="font-semibold bg-gray-100">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groupBy
                      ? sortedData.map((group) => (
                          <TableRow
                            key={group.groupKey}
                            className="hover:bg-gray-50"
                          >
                            <TableCell>{group.groupKey}</TableCell>
                            <TableCell>{group.count}</TableCell>
                            <TableCell>
                              ${group.totalAmount.toFixed(2)}
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  // Expand group logic
                                  alert(`View details for ${group.groupKey}`);
                                }}
                              >
                                <FaEye />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      : sortedData.map((expense) => (
                          <TableRow
                            key={expense.id}
                            className="hover:bg-gray-50"
                          >
                            {selectedColumns.map((column) => (
                              <TableCell key={column}>
                                {column === "date"
                                  ? new Date(expense[column])
                                      .toLocaleDateString("en-US", {
                                        month: "2-digit",
                                        day: "2-digit",
                                        year: "numeric",
                                      })
                                      .replace(/\//g, "-")
                                  : column === "amount"
                                  ? `$${expense[column].toFixed(2)}`
                                  : expense[column]}
                              </TableCell>
                            ))}
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  // View expense details
                                  alert(
                                    `View details for ${expense.expense_title}`
                                  );
                                }}
                              >
                                <FaEye />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {viewMode === "bar" && (
              <div className="p-4 h-[500px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 60,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      interval={0}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis
                      label={{
                        value: "Amount ($)",
                        angle: -90,
                        position: "insideLeft",
                      }}
                      tickFormatter={(value) => `$${value}`}
                    />
                    <RechartsTooltip
                      formatter={(value) => [`$${value.toFixed(2)}`, "Amount"]}
                      labelFormatter={(label) => `${label}`}
                    />
                    <Legend verticalAlign="top" height={36} />
                    <Bar
                      dataKey="value"
                      name="Amount"
                      fill="#8884d8"
                      radius={[4, 4, 0, 0]}
                    >
                      {chartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {viewMode === "pie" && (
              <div className="p-4 h-[500px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      activeIndex={activeIndex}
                      activeShape={renderActiveShape}
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={120}
                      dataKey="value"
                      onMouseEnter={onPieEnter}
                    >
                      {chartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      formatter={(value) => [`$${value.toFixed(2)}`, "Amount"]}
                      labelFormatter={(label) => `${label}`}
                    />
                    <Legend
                      layout="vertical"
                      verticalAlign="middle"
                      align="right"
                      formatter={(value, entry, index) => {
                        const { name, value: amount } = chartData[index];
                        return `${name}: $${amount.toFixed(2)}`;
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        )}
      </div>

      {/* Save Configuration Modal */}
      {showSaveModal && (
        <Modal
          open={showSaveModal}
          onClose={() => setShowSaveModal(false)}
          title="Save Report Configuration"
        >
          <div className="p-4">
            <TextField
              label="Configuration Name"
              value={configName}
              onChange={(e) => setConfigName(e.target.value)}
              fullWidth
              className="mb-4"
            />

            <div className="flex justify-end space-x-2">
              <Button
                variant="outlined"
                onClick={() => setShowSaveModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={saveConfiguration}
                disabled={!configName}
                className="bg-blue-600"
              >
                Save
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Saved Configurations List */}
      {savedConfigurations.length > 0 && (
        <div className="mt-6">
          <h3 className="mb-2 text-lg font-medium text-gray-800">
            Saved Configurations
          </h3>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {savedConfigurations.map((config, index) => (
              <div
                key={index}
                className="p-3 border rounded-md hover:bg-gray-50"
              >
                <p className="font-medium">{config.name}</p>
                <div className="flex justify-end mt-2">
                  <Button
                    size="small"
                    onClick={() => loadConfiguration(config.config)}
                    className="text-blue-600"
                  >
                    Load
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseFlexiReportsChild;
